import { useAuth0 } from "@auth0/auth0-react";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { setUserContext } from "../telemetry/otel";
import {
  CURRENT_USER_QUERY_KEY,
  GC_TIME_MS,
  MAX_RETRY_ATTEMPTS,
  STALE_TIME_MS,
} from "./constants";

interface UseCurrentUserOptions {
  enabled?: boolean;
}

interface UseCurrentUserResult {
  user: UserManagement_V1.UserWithCompanyName | undefined;
  isLoading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  refetch: () => void;
}

export const useCurrentUser = (
  options: UseCurrentUserOptions = {},
): UseCurrentUserResult => {
  const { enabled = true } = options;

  const {
    isAuthenticated,
    isLoading: authLoading,
    user: auth0USer,
  } = useAuth0();
  const userManagementService = useUserManagementServiceV1();

  const queryKey = useMemo(
    () => [CURRENT_USER_QUERY_KEY, auth0USer?.email],
    [auth0USer?.email],
  );

  const isQueryEnabled = useMemo(
    () => enabled && isAuthenticated && !authLoading && !!auth0USer?.email,
    [enabled, isAuthenticated, authLoading, auth0USer?.email],
  );

  const {
    data: user,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => userManagementService.getCurrentUser(),
    enabled: isQueryEnabled,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: (failureCount, err) => {
      if ("status" in err && (err.status === 401 || err.status === 403)) {
        return false;
      }

      return failureCount < MAX_RETRY_ATTEMPTS;
    },
  });

  useEffect(() => {
    if (user) {
      setUserContext({
        userId: user.userId,
        companyId: user.companyId,
        email: user.emailAddress,
      });
    }
  }, [user]);

  const handleRefetch = () => {
    void refetch();
  };

  return {
    user,
    isLoading: authLoading || isLoading,
    error,
    isAuthenticated,
    refetch: handleRefetch,
  };
};
